import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import {
  OrbiAgentResponse,
  OrbiFeedbackRequest,
  OrbiHistoryConversation,
  OrbiRequest,
  OrbiSubmitFeedbackResponse,
  OrbiUpdateHistoryRequest,
} from '@patient-ui/shared/models';
import { EnvironmentService } from '@patient-ui/shared-ui/utils';

import { mapOrbiError } from './orbi-error';

/**
 * HTTP client for the Orbi AI agent (Moonshot AI gateway).
 *
 * Covers the full endpoint surface the mobile app uses (parity — it is NOT a
 * single endpoint): `POST /orbi-agent`, `GET/PATCH/DELETE /history`, and
 * `POST /history/{sessionId}/feedback`. See docs/orbi/moonshot-api-reference.md.
 *
 * Conventions (match the existing store services, e.g. ResultsService):
 *  - `providedIn: 'root'`, inject `HttpClient` + `EnvironmentService`.
 *  - Every endpoint normalizes transport failures into a typed [OrbiError] via
 *    `mapOrbiError` (so a `503/504` is `Transient`/retryable, an offline /
 *    status-0 failure is `Connection`, `401/403` is `Auth`, etc.). This is the
 *    "detectable error condition that the web client can handle" the story
 *    requires (PCSA-13163 AC7); deciding what to DO with it (retry copy, error
 *    bubble) remains the NgRx effect's responsibility.
 *
 * Auth: requests go to a same-API-host `/protected/orbi/*` path, so the existing
 * httpOnly portal cookie rides automatically (PatientInterceptor sets
 * `withCredentials: true`). The API Gateway route validates that cookie and
 * injects the Okta JWT as a server-side `Authorization: Bearer` header to the
 * Moonshot gateway — the token is never handled in browser JS. Services set no
 * auth headers themselves.
 */
@Injectable({
  providedIn: 'root',
})
export class OrbiService {
  constructor(
    private readonly http: HttpClient,
    private readonly envService: EnvironmentService
  ) {}

  /**
   * True when the Orbi gateway is configured for the current environment.
   * When false the feature is disabled (parity with mobile's null base URL),
   * and callers must not issue requests.
   */
  get isConfigured(): boolean {
    return !!this.envService.orbiApiBaseUrl;
  }

  /** Sends a user message to the agent and returns its response. */
  sendMessage(request: OrbiRequest): Observable<OrbiAgentResponse> {
    const baseUrl = this.requireBaseUrl();
    if (typeof baseUrl !== 'string') {
      return baseUrl;
    }
    return this.mapErrors(
      this.http.post<OrbiAgentResponse>(`${baseUrl}/orbi-agent`, request)
    );
  }

  /** Fetches a paginated page of past conversations. */
  fetchHistory(
    skip: number,
    take: number
  ): Observable<OrbiHistoryConversation[]> {
    const baseUrl = this.requireBaseUrl();
    if (typeof baseUrl !== 'string') {
      return baseUrl as Observable<OrbiHistoryConversation[]>;
    }
    const params = new HttpParams()
      .set('skip', String(skip))
      .set('take', String(take));
    return this.mapErrors(
      this.http.get<OrbiHistoryConversation[]>(`${baseUrl}/history`, {
        params,
      })
    );
  }

  /** Renames a conversation. */
  renameConversation(sessionId: string, title: string): Observable<unknown> {
    const baseUrl = this.requireBaseUrl();
    if (typeof baseUrl !== 'string') {
      return baseUrl;
    }
    const body: OrbiUpdateHistoryRequest = { title };
    return this.mapErrors(
      this.http.patch(
        `${baseUrl}/history/${encodeURIComponent(sessionId)}`,
        body
      )
    );
  }

  /** Deletes a conversation. */
  deleteConversation(sessionId: string): Observable<unknown> {
    const baseUrl = this.requireBaseUrl();
    if (typeof baseUrl !== 'string') {
      return baseUrl;
    }
    return this.mapErrors(
      this.http.delete(`${baseUrl}/history/${encodeURIComponent(sessionId)}`)
    );
  }

  /** Submits thumbs up/down feedback for a message in a conversation. */
  submitFeedback(
    sessionId: string,
    feedback: OrbiFeedbackRequest
  ): Observable<OrbiSubmitFeedbackResponse> {
    const baseUrl = this.requireBaseUrl();
    if (typeof baseUrl !== 'string') {
      return baseUrl as Observable<OrbiSubmitFeedbackResponse>;
    }
    return this.mapErrors(
      this.http.post<OrbiSubmitFeedbackResponse>(
        `${baseUrl}/history/${encodeURIComponent(sessionId)}/feedback`,
        feedback
      )
    );
  }

  /**
   * Returns the configured base URL, or a throwing Observable when the feature
   * is disabled for this environment (parity with mobile, which throws
   * `OrbiChatException('Moonshot AI API base URL is not configured')`).
   */
  private requireBaseUrl(): string | Observable<never> {
    const baseUrl = this.envService.orbiApiBaseUrl;
    if (!baseUrl) {
      return throwError(() => new Error('Orbi API base URL is not configured'));
    }
    return baseUrl;
  }

  /**
   * Normalizes any transport failure into a typed [OrbiError] (PCSA-13163 AC7).
   * Applied to every endpoint so connection/auth/transient/bad-request
   * conditions are detectable by callers instead of leaking a raw
   * `HttpErrorResponse`. The NgRx effect decides how to react (retry, error
   * bubble) — parity with mobile, where the service classifies and the UI
   * reacts.
   */
  private mapErrors<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      catchError((error: unknown) => throwError(() => mapOrbiError(error)))
    );
  }
}
